import React, { useEffect, useState } from 'react';
import {
  Form,
  FormInstance,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';

import { CardBlock } from '../../card';
import { LoadingBlock } from '../../loading/progress';
import Modal from '../../modal';
import Toast from '../../toast';
import Callout from '../../callout';

import { Col, Row } from '../../../../grid';
import Button from '../../../molecules/button';
import Icon from '../../../atoms/icon';
import { faSave } from '@fortawesome/pro-solid-svg-icons';
import { ConfigComponent } from '../../../../libs/apps';
import { PostJSON, BaseResponse } from '../../../../libs/request';
import { useMetaData } from '../../../../contexts/metadata';
import { useGetAccountSettings } from '../../../../libs/account';

interface GenericConfigFormData {
  programCommentFlow: {
    [key: string]: boolean;
  };
}

/**
 * Composer Program Configuration Form
 *
 * @returns JSX.Element
 */
export const GenericConfigForm: ConfigComponent = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState<GenericConfigFormData>(null);
  const { terms } = useMetaData();
  const { settings: existingSettings, isLoading: isDataLoading } =
    useGetAccountSettings();
  const appName = 'customPrograms';
  let initialValue = false;
  if (
    existingSettings?.programCommentFlow &&
    existingSettings.programCommentFlow[appName]
  ) {
    initialValue = existingSettings.programCommentFlow[appName];
  }
  const initialFormData = {
    programCommentFlow: {
      [appName]: initialValue
    }
  };
  const form = new FormInstance<GenericConfigFormData>(initialFormData);

  // Fill in form with updated settings
  useEffect(() => {
    if (settings != null) {
      form.setData(settings);
    }
  }, [settings]);

  /**
   * Save account settings
   */
  const onSubmit = (data: GenericConfigFormData) => {
    const params = {
      programCommentFlow: {
        [appName]: data.programCommentFlow[appName]
      }
    };
    setIsLoading(true);
    PostJSON<BaseResponse>({
      url: '/user/edit_account_settings/',
      params
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Saving Settings',
          content: response.message
        });
        return;
      }
      Toast({
        intent: 'success',
        message: 'The settings have been saved successfully!'
      });
      setSettings(data);
    });
  };

  if (isLoading) {
    return <LoadingBlock />;
  }

  return isDataLoading || isLoading ? null : (
    <Form form={form} onSubmit={onSubmit}>
      <>
        <CardBlock>
          <Row>
            <Col sm={24}>
              <SelectBooleanInput
                label="Enable Program Comment Flow"
                name={`programCommentFlow[${appName}]`}
                fill
              />
              <Callout>
                Turning this on adds an optional post-check-in SMS comment flow
                for schema-based programs, allowing {terms.patient.plural} to
                report additional issues in freeform text.
              </Callout>
            </Col>
          </Row>
          <Row className="mt-7">
            <Col sm={8}>
              <Button
                type="submit"
                icon={<Icon icon={faSave} />}
                intent="primary"
                fill
              >
                Save
              </Button>
            </Col>
          </Row>
        </CardBlock>
      </>
    </Form>
  );
};
